import { computed, ref } from 'vue'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { getTopLinks } from '@/js/stores/category-links'
import type { Category , IRogue } from '@/js/services/RogueTypes'

const COMPARE_LS_KEY = 'category_compare'

function saveCompare(compare: unknown) {
  try {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(COMPARE_LS_KEY, JSON.stringify(compare))
    }
  } catch (e) {
    //
  }
}

export type CategoryStoreState = {
  categories: { [key: string]: Category },
  byUrl: { [key: string]: number | undefined },
  compare: { [key: string]: number[] },
}

export const useCategoryStore = defineStore('categories', () => {
  const loadData: { [key: string]: undefined | Promise<void | Category> } = {}

  const categories = ref<CategoryStoreState['categories']>({})
  const byUrl = ref<CategoryStoreState['byUrl']>({})
  const compare = ref<CategoryStoreState['compare']>({})

  // Raw category items from the shop menu endpoint (categories with show-in-menu
  // set). The endpoint already returns only menu categories, so every id here is
  // a "show in menu" category.
  const menuItems = ref<any[]>([])
  let menuLoad: Promise<Record<string, any>> | undefined

  // Set of category IDs that appear in the shop menu. Used to restrict which
  // category facet values are shown on the search page.
  const menuCategoryIds = computed(() => new Set<number>(menuItems.value.map(item => item.id)))

  // Names of the shop menu's top-level categories (the menu root's direct children). These are
  // the catalog names, so they match the `category` attribute the deals page facets on.
  const menuTopLevelNames = computed(() => {
    const root = menuItems.value.find(item => item.parent === 1 && item.id !== 2)

    return new Set<string>(menuItems.value.filter(item => root && item.parent === root.id).map(item => item.name))
  })

  function loadMenu(rogue: IRogue): Promise<Record<string, any>> {
    if (menuLoad) {
      return menuLoad
    }

    if (menuItems.value.length) {
      return Promise.resolve(Object.fromEntries(menuItems.value.map(item => [item.id, item])))
    }

    menuLoad = rogue.api.fetch<Record<string, any>>({
      url   : '/svc_content/catalog/category/menu',
      cache : { key: 'megamenu', seconds: 60 * 60 },
    })
      .then((menuData) => {
        menuItems.value = Object.values(menuData)

        return menuData
      })
      .catch((err) => {
        menuLoad = undefined
        rogue.log.error('Unable to load menu', err.response?.body || err.message || err)
        throw new Error('Unable to load menu')
      })

    return menuLoad
  }

  function setIdForUrl(id: number, url: string) {
    if (!byUrl.value[url]) {
      byUrl.value[url] = id
    }
  }

  function eraseIdForUrl(url: string) {
    byUrl.value[url] = undefined
  }

  function clearCompare(categoryId: number) {
    const comp = compare.value
    comp[categoryId] = []
    saveCompare(comp)
  }

  function toggleCompare(categoryId: number, productId: number) {
    const comp = compare.value

    if (!comp[categoryId]) {
      comp[categoryId] = []
    }

    const index = comp[categoryId].indexOf(productId)

    if (index === -1) {
      comp[categoryId].push(productId)
    } else {
      comp[categoryId].splice(index, 1)
    }

    saveCompare(comp)
  }

  async function load(categoryId: number, rogue: IRogue) {
    if (categories.value[categoryId]) {
      return Promise.resolve(categories.value[categoryId])
    }
    if (categoryId === undefined) {
      return Promise.resolve()
    }

    if (!loadData[categoryId]) {
      loadData[categoryId] = rogue.api.fetch<Category>({
        url   : `/svc_content/catalog/category/${categoryId}`,
        cache : { key: `category-${categoryId}`, seconds: 60 * 5 },
      })
        .then((category: Category) => {
          loadData[categoryId] = undefined

          if (category?.name === 'Equipment for CrossFit®') {
            category.catalogName = category.name
            category.name = 'CrossFit® Equipment'
          }

          if (category?.name === 'Concept 2 Rowers') {
            category.catalogName = category.name
            category.name = 'Rowers'
          }

          try {
            const links = getTopLinks(category, rogue.site.code, rogue.l10n.path, rogue)
            if (links.type) {
              category.topLinks = links
            }
          } catch (e) {
            rogue.log.warn('Top links Error', e as Error)
            //
          }

          categories.value[category.categoryId] = category
          byUrl.value[category.url] = category.categoryId

          return category
        })
        .catch((e: any) => {
          loadData[categoryId] = undefined
          
          if (e.message !== 'browser_offline') {
            // IRogueLog.warn expects a Record<string, any>; passing a raw
            // string or axios response object can serialize as [object Object]
            // because of circular refs / non-plain values. Wrap with named
            // keys, and stringify defensively (axios response objects can
            // have cycles through e.response.request).
            let dataStr: string | undefined
            const responseData = e.response?.data
            if (typeof responseData === 'string') {
              dataStr = responseData
            } else if (responseData) {
              try {
                dataStr = JSON.stringify(responseData)
              } catch {
                dataStr = '[unserializable response]'
              }
            }
            rogue.log.warn(`Error loading category ${categoryId}`, {
              message : e.message,
              status  : e.response?.status,
              data    : dataStr,
            })
          }
        })
    }

    return loadData[categoryId] || Promise.resolve()
  }

  /**
   * Load a list of categoryIds. If one fails, it does NOT error the full promise
   */
  async function loadAll(categoryIds: (number | undefined)[], rogue: IRogue) {
    await Promise.allSettled(categoryIds.filter((id): id is number => id !== undefined).map(id => load(id, rogue)))
  }

  function initCompare() {
    try {
      compare.value = JSON.parse(window.localStorage.getItem(COMPARE_LS_KEY) || '{}')
    } catch (e) {
      //
    }
  }

  return {
    categories,
    byUrl,
    compare,
    menuItems,
    menuCategoryIds,
    menuTopLevelNames,
    setIdForUrl,
    eraseIdForUrl,
    clearCompare,
    toggleCompare,
    load,
    loadAll,
    loadMenu,
    initCompare,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useCategoryStore, import.meta.hot))
}
